import { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Calendar, MapPin, Users, Plus, Edit, Trash2, Mail, Eye, X } from 'lucide-react';
import Modal from './Modal';
import AdminDateInput from './AdminDateInput';
import ConfirmDialog from './ConfirmDialog';
import ContentImageInput from './ContentImageInput';

type EventVisibility = 'ALL' | 'PUBLIC' | 'MEMBERS';

interface Event {
  id: number;
  title: string;
  description: string;
  date: string;
  location: string | null;
  maxAttendees: number | null;
  imageUrl: string | null;
  isActive: boolean;
  isPublic: boolean;
  visibility: EventVisibility;
  attendeeCount: number;
  isFull: boolean;
  attendees: Array<{
    user: {
      id: number;
      firstName: string;
      lastName: string;
      email: string | null;
      beltLevel?: string;
      isAdmin?: boolean;
    };
  }>;
  publicRegistrations?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    message: string;
    createdAt: string;
  }>;
  withdrawals?: Array<{ id: number; firstName: string; lastName: string; email: string | null; reason: string; kind: string; createdAt: string }>;
}

interface EventFormData {
  title: string;
  description: string;
  date: string;
  location: string;
  maxAttendees: string;
  imageUrl: string;
  isActive: boolean;
  visibility: EventVisibility;
}

export default function EventsAdminModule() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [registrationToRemove, setRegistrationToRemove] = useState<{ kind: 'member' | 'public'; eventId: number; targetId: number; label: string } | null>(null);
  const [removingRegistration, setRemovingRegistration] = useState(false);
  const [withdrawalReason, setWithdrawalReason] = useState('');
  const [emailForm, setEmailForm] = useState({
    subject: '',
    message: '',
    sendToActiveOnly: true
  });

  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    date: '',
    location: '',
    maxAttendees: '',
    imageUrl: '',
    isActive: true,
    visibility: 'MEMBERS'
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/events/admin');
      setEvents(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des événements:', error);
      toast.error('Erreur lors du chargement des événements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        maxAttendees: formData.maxAttendees ? parseInt(formData.maxAttendees) : null
      };

      if (editingEvent) {
        await axios.put(`/api/events/${editingEvent.id}`, data);
        toast.success('Événement modifié avec succès');
      } else {
        await axios.post('/api/events', data);
        toast.success('Événement créé avec succès');
      }

      setShowForm(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/events/${eventToDelete.id}`);
      toast.success('Événement supprimé avec succès');
      setEventToDelete(null);
      await fetchEvents();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedEvent) return;
    
    try {
      await axios.post(`/api/events/${selectedEvent.id}/send-email`, emailForm);
      toast.success('Email envoyé avec succès');
      setShowEmailModal(false);
      setEmailForm({ subject: '', message: '', sendToActiveOnly: true });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi de l\'email');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      date: '',
      location: '',
      maxAttendees: '',
      imageUrl: '',
      isActive: true,
      visibility: 'MEMBERS'
    });
  };

  const openEventModal = (event: Event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const openEmailModal = (event: Event) => {
    setSelectedEvent(event);
    setEmailForm({
      subject: `Événement : ${event.title}`,
      message: `Bonjour,\n\nNous vous informons de l'événement "${event.title}" qui aura lieu le ${new Date(event.date).toLocaleDateString('fr-FR')}${event.location ? ` à ${event.location}` : ''}.\n\n${event.description}\n\nCordialement,\nL'équipe Taijitsu Dromois`,
      sendToActiveOnly: true
    });
    setShowEventModal(false);
    setShowEmailModal(true);
  };

  const closeEventForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    resetForm();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTimeLocal = (dateString: string) => {
    const date = new Date(dateString);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return localDate.toISOString().slice(0, 16);
  };

  // Ajoute la fonction de désinscription
  const handleUnregister = async (eventId: number, userId: number) => {
    try {
      await axios.delete(`/api/events/${eventId}/attendees/${userId}`, { data: { reason: withdrawalReason } });
      toast.success('Membre désinscrit');
      // Rafraîchir l'événement sélectionné
      if (selectedEvent) {
        const response = await axios.get(`/api/events/admin`);
        const updated = response.data.find((e: any) => e.id === eventId) || null;
        setSelectedEvent(updated);
        fetchEvents();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de la désinscription');
    }
  };

  // Fonction pour supprimer une inscription publique
  const handleDeletePublicRegistration = async (eventId: number, registrationId: number) => {
    try {
      await axios.delete(`/api/events/${eventId}/public-registrations/${registrationId}`, { data: { reason: withdrawalReason } });
      toast.success('Inscription publique supprimée');
      // Rafraîchir l'événement sélectionné
      if (selectedEvent) {
        const response = await axios.get(`/api/events/admin`);
        const updated = response.data.find((e: any) => e.id === eventId) || null;
        setSelectedEvent(updated);
        fetchEvents();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const confirmRegistrationRemoval = async () => {
    if (!registrationToRemove) return;
    setRemovingRegistration(true);
    if (registrationToRemove.kind === 'member') await handleUnregister(registrationToRemove.eventId, registrationToRemove.targetId);
    else await handleDeletePublicRegistration(registrationToRemove.eventId, registrationToRemove.targetId);
    setRemovingRegistration(false);
    setRegistrationToRemove(null);
    setWithdrawalReason('');
    setShowEventModal(true);
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Chargement des événements...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="admin-module-toolbar">
        <div><p className="eyebrow !mb-1">Agenda</p><h3 className="text-xl font-extrabold text-gray-900">Événements programmés</h3></div>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingEvent(null);
            resetForm();
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={20} />
          Nouvel événement
        </button>
      </div>

      {/* Formulaire d'événement */}
      <Modal
        isOpen={showForm}
        onClose={closeEventForm}
        title={editingEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
        eyebrow="Agenda du dojo"
        size="lg"
        footer={(
          <>
            <button type="button" onClick={closeEventForm} className="btn-secondary">Annuler</button>
            <button type="submit" form="event-admin-form" className="btn-primary">{editingEvent ? 'Mettre à jour' : 'Créer'}</button>
          </>
        )}
      >
            <form id="event-admin-form" onSubmit={handleSubmit} className="space-y-5">
              <div className="form-field">
                <label className="form-label" htmlFor="event-title">Titre *</label>
                <input
                  id="event-title"
                  type="text"
                  required
                  minLength={3}
                  placeholder="Ex. Stage régional de Taï-Jitsu"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="input-field w-full"
                />
              </div>
              
              <div className="form-field">
                <label className="form-label" htmlFor="event-description">Description *</label>
                <textarea
                  id="event-description"
                  required
                  minLength={10}
                  placeholder="Présentez le programme et les informations utiles…"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  className="input-field w-full h-32"
                />
              </div>
              
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="form-field">
                  <label className="form-label" htmlFor="event-date">Date et heure *</label>
                  <AdminDateInput
                    id="event-date"
                    required
                    includeTime
                    value={formData.date}
                    onChange={date => setFormData({ ...formData, date })}
                    className="w-full"
                  />
                </div>
                
                <div className="form-field">
                  <label className="form-label" htmlFor="event-location">Lieu</label>
                  <input
                    id="event-location"
                    type="text"
                    placeholder="Ex. Dojo de l’Olivette"
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                    className="input-field w-full"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="form-field">
                  <label className="form-label" htmlFor="event-capacity">Nombre max. de participants</label>
                  <input
                    id="event-capacity"
                    type="number"
                    min="1"
                    value={formData.maxAttendees}
                    onChange={e => setFormData({ ...formData, maxAttendees: e.target.value })}
                    className="input-field w-full"
                  />
                </div>
                
                <div className="form-field">
                  <label className="form-label" htmlFor="event-image">Image <span className="form-label-optional">(facultative)</span></label>
                  <ContentImageInput id="event-image" value={formData.imageUrl} onChange={imageUrl => setFormData({ ...formData, imageUrl })} />
                </div>
              </div>
              
              <div className="form-check-grid sm:grid-cols-2">
                <label className="form-check-card" htmlFor="event-is-active">
                  <input type="checkbox" id="event-is-active" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                  <span>Événement actif</span>
                </label>
                <div className="form-field">
                  <label className="form-label" htmlFor="event-visibility">Audience</label>
                  <select id="event-visibility" className="input-field w-full" value={formData.visibility} onChange={e => setFormData({ ...formData, visibility: e.target.value as EventVisibility })}>
                    <option value="ALL">Tout le monde</option>
                    <option value="PUBLIC">Visiteurs non connectés</option>
                    <option value="MEMBERS">Membres connectés</option>
                  </select>
                </div>
              </div>
            </form>
      </Modal>

      {/* Modal de détail d'événement */}
      {selectedEvent && <Modal isOpen={showEventModal} onClose={() => setShowEventModal(false)} title={selectedEvent.title} eyebrow="Détail de l’événement" size="lg">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar size={16} />
                <span>{formatDate(selectedEvent.date)}</span>
              </div>
              
              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin size={16} />
                  <span>{selectedEvent.location}</span>
                </div>
              )}
              
              <div className="flex items-center gap-2 text-gray-600">
                <Users size={16} />
                <span>
                  {selectedEvent.attendeeCount} inscrit(s)
                  {selectedEvent.maxAttendees && ` / ${selectedEvent.maxAttendees} max`}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Statut :</span>
                {selectedEvent.isActive ? (
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    Actif
                  </span>
                ) : (
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    Inactif
                  </span>
                )}
                <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                  {selectedEvent.visibility === 'ALL' ? 'Tout le monde' : selectedEvent.visibility === 'PUBLIC' ? 'Visiteurs' : 'Membres'}
                </span>
                {selectedEvent.isFull && (
                  <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                    Complet
                  </span>
                )}
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedEvent.description}</p>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Liste des inscrits ({selectedEvent.attendees.length + (selectedEvent.publicRegistrations?.length || 0)} total)</h3>
                
                {/* Inscriptions des adhérents */}
                {selectedEvent.attendees.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Adhérents ({selectedEvent.attendees.length})</h4>
                    <div className="space-y-2">
                      {selectedEvent.attendees.map((attendee) => (
                        <div key={attendee.user.id} className="modal-section flex items-center justify-between p-3">
                          <div>
                            <span className="font-medium">{attendee.user.firstName} {attendee.user.lastName}</span>
                            {attendee.user.email && <span className="text-gray-500 ml-2">({attendee.user.email})</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">
                              {attendee.user.beltLevel} {attendee.user.isAdmin && '(Administrateur)'}
                            </span>
                            <button
                              className="text-red-600 hover:text-red-900 ml-2"
                              title="Désinscrire"
                              onClick={() => {
                                setRegistrationToRemove({ kind: 'member', eventId: selectedEvent.id, targetId: attendee.user.id, label: `${attendee.user.firstName} ${attendee.user.lastName}` });
                                setShowEventModal(false);
                              }}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inscriptions publiques */}
                {selectedEvent.publicRegistrations && selectedEvent.publicRegistrations.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Inscriptions publiques ({selectedEvent.publicRegistrations.length})</h4>
                    <div className="space-y-2">
                      {selectedEvent.publicRegistrations.map((registration) => (
                        <div key={registration.id} className="modal-section flex items-center justify-between p-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{registration.firstName} {registration.lastName}</span>
                              <span className="text-gray-500">({registration.email})</span>
                              <span className="text-xs text-gray-400">
                                {new Date(registration.createdAt).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            {registration.message && (
                              <div className="text-sm text-gray-600 mt-1">
                                "{registration.message}"
                              </div>
                            )}
                          </div>
                          <button
                            className="text-red-600 hover:text-red-900 ml-2"
                            title="Supprimer"
                            onClick={() => {
                              setRegistrationToRemove({ kind: 'public', eventId: selectedEvent.id, targetId: registration.id, label: `${registration.firstName} ${registration.lastName}` });
                              setShowEventModal(false);
                            }}
                          >
                            <X size={18} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedEvent.attendees.length === 0 && (!selectedEvent.publicRegistrations || selectedEvent.publicRegistrations.length === 0) && (
                  <p className="text-gray-500 text-center py-4">Aucun inscrit</p>
                )}
                {selectedEvent.withdrawals && selectedEvent.withdrawals.length > 0 && (
                  <div className="mt-5 border-t border-primary-900/10 pt-4">
                    <h4 className="mb-2 text-sm font-bold text-gray-900">Désistements ({selectedEvent.withdrawals.length})</h4>
                    <div className="space-y-2">
                      {selectedEvent.withdrawals.map(withdrawal => (
                        <div key={withdrawal.id} className="modal-section p-3 text-sm">
                          <div className="flex flex-wrap justify-between gap-2"><strong>{withdrawal.firstName} {withdrawal.lastName}</strong><span className="text-gray-500">{new Date(withdrawal.createdAt).toLocaleDateString('fr-FR')}</span></div>
                          <p className="mt-1 whitespace-pre-wrap text-gray-700">{withdrawal.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={() => openEmailModal(selectedEvent)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Mail size={16} />
                  Envoyer un email
                </button>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="btn-primary"
                >
                  Fermer
                </button>
              </div>
            </div>
      </Modal>}

      {/* Modal d'envoi d'email */}
      <Modal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} title="Envoyer un email" eyebrow="Communication événement" size="md">
            <form onSubmit={(e) => { e.preventDefault(); handleSendEmail(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sujet *</label>
                <input
                  type="text"
                  required
                  value={emailForm.subject}
                  onChange={e => setEmailForm({ ...emailForm, subject: e.target.value })}
                  className="input-field w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                <textarea
                  required
                  value={emailForm.message}
                  onChange={e => setEmailForm({ ...emailForm, message: e.target.value })}
                  className="input-field w-full h-32"
                  placeholder="Vous pouvez utiliser {{firstName}}, {{lastName}}, {{eventTitle}}, {{eventDate}}, {{eventLocation}} pour personnaliser le message"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendToActiveOnly"
                  checked={emailForm.sendToActiveOnly}
                  onChange={e => setEmailForm({ ...emailForm, sendToActiveOnly: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="sendToActiveOnly" className="text-sm font-medium text-gray-700">
                  Envoyer seulement aux adhérents actifs
                </label>
              </div>
              
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowEmailModal(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button type="submit" className="btn-primary">
                  Envoyer
                </button>
              </div>
            </form>
      </Modal>

      {/* Liste des événements */}
      <div className="card !p-0 overflow-hidden">
        <div className="data-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Événement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lieu
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscrits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {events.map(event => (
                <tr key={event.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{event.title}</div>
                      {/* Description supprimée ici pour alléger la liste */}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(event.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {event.location || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {event.attendeeCount}
                    {event.maxAttendees && ` / ${event.maxAttendees}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        {event.isActive ? (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Actif
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                            Inactif
                          </span>
                        )}
                        <span className="rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">{event.visibility === 'ALL' ? 'Tous' : event.visibility === 'PUBLIC' ? 'Visiteurs' : 'Membres'}</span>
                      </div>
                      {event.isFull && (
                        <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                          Complet
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEventModal(event)}
                        className="btn-secondary !px-3 !py-2"
                        title="Voir les détails"
                        aria-label={`Voir ${event.title}`}
                      >
                        <Eye size={16} /> Voir
                      </button>
                      <button
                        onClick={() => {
                          setEditingEvent(event);
                          setFormData({
                            title: event.title,
                            description: event.description,
                            date: formatDateTimeLocal(event.date),
                            location: event.location || '',
                            maxAttendees: event.maxAttendees?.toString() || '',
                            imageUrl: event.imageUrl || '',
                            isActive: event.isActive,
                            visibility: event.visibility
                          });
                          setShowForm(true);
                        }}
                        className="btn-secondary !px-3 !py-2"
                        title="Modifier"
                        aria-label={`Modifier ${event.title}`}
                      >
                        <Edit size={16} /> Modifier
                      </button>
                      <button
                        onClick={() => setEventToDelete(event)}
                        className="btn-danger !px-3 !py-2"
                        title="Supprimer"
                        aria-label={`Supprimer ${event.title}`}
                      >
                        <Trash2 size={16} /> Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {events.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Aucun événement à afficher
          </div>
        )}
      </div>
      <ConfirmDialog isOpen={eventToDelete !== null} title="Supprimer l’événement" message={`L’événement « ${eventToDelete?.title ?? ''} » et ses inscriptions seront définitivement supprimés.`} confirmLabel="Supprimer" isBusy={deleting} onConfirm={handleDelete} onCancel={() => setEventToDelete(null)} />
      <Modal isOpen={registrationToRemove !== null} title="Motif du désistement" eyebrow={registrationToRemove?.label ?? ''} onClose={() => { setRegistrationToRemove(null); setWithdrawalReason(''); setShowEventModal(true); }} footer={<><button type="button" className="btn-secondary" onClick={() => { setRegistrationToRemove(null); setWithdrawalReason(''); setShowEventModal(true); }}>Annuler</button><button type="button" className="btn-danger" disabled={withdrawalReason.trim().length < 3 || removingRegistration} onClick={() => void confirmRegistrationRemoval()}>{removingRegistration ? 'Suppression…' : 'Confirmer le désistement'}</button></>}>
        <label className="form-label" htmlFor="admin-withdrawal-reason">Motif obligatoire</label>
        <textarea id="admin-withdrawal-reason" className="input-field min-h-28 w-full" maxLength={1000} required value={withdrawalReason} onChange={event => setWithdrawalReason(event.target.value)} placeholder="Indiquez la raison du désistement…" />
        <p className="mt-2 text-right text-xs text-gray-500">{withdrawalReason.length}/1 000</p>
      </Modal>
    </div>
  );
}
